import _ from 'underscore';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { Select } from '@bingads-webui-react/primitive';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { PromotionAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import { InputWithExtra } from '@bingads-webui-campaign/ad-extensions-shared';
import {
  promotionTypesMap, isMonetary, getPromotionTypeForModel, cleanPromotionTypeData,
} from '../rules/promotion-type';

const promotionTypes = {
  [promotionTypesMap.MonetaryDiscount]: _TL_('Monetary discount'),
  [promotionTypesMap.PercentDiscount]: _TL_('Percent discount'),
  [promotionTypesMap.UptoMonetaryDiscount]: _TL_('Up to monetary discount'),
  [promotionTypesMap.UptoPercentDiscount]: _TL_('Up to percent discount'),
};

function PromotionTypeSelect({
  className, id, onChange, value: selected, i18n,
}) {
  const [selectedPromotionType, setSelectedPromotionType] = useState(selected);

  return (
    <Select
      id={id}
      className={className}
      value={selectedPromotionType}
      onChange={(event) => {
        const language = event.target.value;
        setSelectedPromotionType(language);
        onChange(language);
      }}
    >
      {_.pairs(promotionTypes).map(([key, value]) =>
        (
          <option key={key} value={key}>
            {i18n.getString(value)}
          </option>
        ))}
    </Select>
  );
}

PromotionTypeSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

PromotionTypeSelect.defaultProps = {
  id: undefined,
  value: promotionTypesMap.MonetaryDiscount,
};

const SubSchema = cloneDeep({
  properties: {
    ..._.pick(PromotionAdExtension.properties, 'DiscountModifier', 'MoneyAmountOff', 'PercentOff'),
  },
});

const Schema = (promotionType) => {
  const schema = cloneDeep(SubSchema);

  schema.properties.MoneyAmountOff.required = isMonetary(promotionType);
  schema.properties.PercentOff.required = !isMonetary(promotionType);

  return schema;
};

export function PromotionType({
  value, onChange, i18n, legacyI18n, id, promotionTypeSelectClassName,
  inputContainerClass, inputClass, inputExtraClass,
}) {
  const [promotionType, setPromotionType] = useState(getPromotionTypeForModel(value));
  const [data, setData] =
    useState({ ...cleanPromotionTypeData(promotionType, value), promotionType });
  const [schema, setSchema] = useState(Schema(promotionType));

  const onPromotionTypeChange = (newPromotionType) => {
    setData((currentData) => {
      const mergedData = {
        ...currentData,
        ...cleanPromotionTypeData(newPromotionType, currentData),
        // Injecting UI state to help interconnected components, e.g. Currency
        promotionType: newPromotionType,
      };
      setPromotionType(newPromotionType);
      setSchema(Schema(newPromotionType));
      onChange(mergedData);
      return mergedData;
    });
  };

  const onValidate = ({ data: newData }) => {
    setData((currentData) => {
      const mergedData = { ...currentData, ...newData };
      onChange(mergedData);
      return mergedData;
    });
  };

  return (
    <React.Fragment>
      <PromotionTypeSelect
        className={classnames(promotionTypeSelectClassName, 'promotion-extension-editor__promotion-type')}
        id={id}
        value={promotionType}
        onChange={onPromotionTypeChange}
        i18n={i18n}
      />
      <ValidationGroup
        validationProps={{
          schema,
          i18n: legacyI18n,
          data,
          onValidate,
        }}
      >
        {isMonetary(promotionType) &&
          <InputWithExtra
            containerClass={inputContainerClass}
            className={inputClass}
            id="monetary-value"
            validationProps={{
              field: 'MoneyAmountOff',
            }}
            extra={() => <span className={inputExtraClass}>{value.CurrencyCode}</span>}
          />
        }
        {!isMonetary(promotionType) &&
          <InputWithExtra
            containerClass={inputContainerClass}
            className={inputClass}
            id="percent-value"
            validationProps={{
              field: 'PercentOff',
            }}
            extra={() => <span className={inputExtraClass}>%</span>}
          />
        }
      </ValidationGroup>
    </React.Fragment>
  );
}

PromotionType.propTypes = {
  id: PropTypes.string.isRequired,
  promotionTypeSelectClassName: PropTypes.string.isRequired,
  inputContainerClass: PropTypes.string.isRequired,
  inputClass: PropTypes.string.isRequired,
  inputExtraClass: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({}),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

PromotionType.defaultProps = {
  value: undefined,
};
